import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import { qk } from './query-keys';
import type {
  Load,
  LoadMotion,
  LoadStatus,
  LoadStatusHistoryEntry,
  LoadStop,
  LoadTrackingLinkWithToken,
} from '@breaker/shared-types';
import type {
  CreateStopBody,
  UpdateStopBody,
  UpdateLoadBody,
} from '@breaker/shared-schemas';

interface LoadDetailCache {
  load: Load;
  stops: LoadStop[];
}

/**
 * Helper: invalidate every cached query for a fleet's loads (list + each
 * detail). Coarse but cheap; we don't have so many load views that
 * granular invalidation is worth the complexity.
 */
function invalidateFleetLoads(qc: ReturnType<typeof useQueryClient>, fleetId: string) {
  qc.invalidateQueries({ queryKey: qk.loads.all(fleetId) });
}

interface MutationCtx {
  fleetId: string;
}

// -------- Transition --------

interface TransitionVars {
  loadId: string;
  to: LoadStatus;
  reason?: string;
}

export function useTransitionLoad({ fleetId }: MutationCtx) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ loadId, to, reason }: TransitionVars) =>
      api<{ load: Load; history: LoadStatusHistoryEntry }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/transition`,
        {
          method: 'POST',
          body: { to, ...(reason ? { reason } : {}) },
        },
      ),
    onMutate: async ({ loadId, to }) => {
      const key = qk.loads.detail(fleetId, loadId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<LoadDetailCache>(key);
      if (previous) {
        qc.setQueryData<LoadDetailCache>(key, {
          ...previous,
          load: { ...previous.load, status: to },
        });
      }
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.loads.detail(fleetId, vars.loadId), ctx.previous);
      }
    },
    onSuccess: (_data, vars) => {
      // Server confirmed — refetch detail (gets canonical updatedAt) + history.
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      qc.invalidateQueries({
        queryKey: ['loads', fleetId, 'history', vars.loadId],
      });
      // List view also stale (the row's status changed).
      qc.invalidateQueries({ queryKey: qk.loads.all(fleetId) });
    },
  });
}

// -------- Cancel --------

interface CancelVars {
  loadId: string;
  reason: string;
}

export function useCancelLoad({ fleetId }: MutationCtx) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ loadId, reason }: CancelVars) =>
      api<{ load: Load }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/cancel`,
        { method: 'POST', body: { reason } },
      ),
    onMutate: async ({ loadId }) => {
      const key = qk.loads.detail(fleetId, loadId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<LoadDetailCache>(key);
      if (previous) {
        qc.setQueryData<LoadDetailCache>(key, {
          ...previous,
          load: { ...previous.load, status: 'cancelled' },
        });
      }
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.loads.detail(fleetId, vars.loadId), ctx.previous);
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      qc.invalidateQueries({
        queryKey: ['loads', fleetId, 'history', vars.loadId],
      });
      invalidateFleetLoads(qc, fleetId);
    },
  });
}

// -------- Assign --------

interface AssignVars {
  loadId: string;
  driverUserId?: string | null;
  vehicleId?: string | null;
}

export function useAssignLoad({ fleetId }: MutationCtx) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ loadId, driverUserId, vehicleId }: AssignVars) => {
      const body: Record<string, string | null> = {};
      if (driverUserId !== undefined) body['driverUserId'] = driverUserId;
      if (vehicleId !== undefined) body['vehicleId'] = vehicleId;
      return api<{ load: Load }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/assign`,
        { method: 'POST', body },
      );
    },
    onMutate: async ({ loadId, driverUserId }) => {
      // Only optimistically update assignedDriverUserId — vehicleId isn't on
      // the cached Load shape we display.
      if (driverUserId === undefined) return { previous: undefined };
      const key = qk.loads.detail(fleetId, loadId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<LoadDetailCache>(key);
      if (previous) {
        qc.setQueryData<LoadDetailCache>(key, {
          ...previous,
          load: {
            ...previous.load,
            assignedDriverUserId: (driverUserId as Load['assignedDriverUserId']) ?? null,
          },
        });
      }
      return { previous };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(qk.loads.detail(fleetId, vars.loadId), ctx.previous);
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      invalidateFleetLoads(qc, fleetId);
    },
  });
}

// -------- Create load --------

interface CreateLoadVars {
  reference?: string;
  vehicleType?: string | null;
  tailLift?: boolean;
  notesDriver?: string | null;
  customerId?: string | null;
  scheduledStartAt?: string | null;
  serviceChargePence?: number;
  extrasPence?: number;
  vatPence?: number;
  totalPence?: number;
}

export function useCreateLoad({ fleetId }: MutationCtx) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (vars: CreateLoadVars) => {
      const body: Record<string, unknown> = {};
      if (vars.reference) body['reference'] = vars.reference;
      if (vars.customerId) body['customerId'] = vars.customerId;
      if (vars.vehicleType) body['vehicleType'] = vars.vehicleType;
      if (vars.tailLift !== undefined) body['tailLift'] = vars.tailLift;
      if (vars.notesDriver) body['notesDriver'] = vars.notesDriver;
      if (vars.scheduledStartAt) body['scheduledStartAt'] = vars.scheduledStartAt;
      if (vars.serviceChargePence !== undefined) body['serviceChargePence'] = vars.serviceChargePence;
      if (vars.extrasPence !== undefined) body['extrasPence'] = vars.extrasPence;
      if (vars.vatPence !== undefined) body['vatPence'] = vars.vatPence;
      if (vars.totalPence !== undefined) body['totalPence'] = vars.totalPence;
      return api<{ load: Load }>(
        `/v1/fleets/${fleetId}/loads`,
        { method: 'POST', body },
      );
    },
    onSuccess: () => {
      invalidateFleetLoads(qc, fleetId);
    },
  });
}

// -------- Tracking link --------

interface CreateTrackingLinkVars {
  loadId: string;
  label?: string;
  expiresAt?: string;
}

export function useCreateTrackingLink({ fleetId }: MutationCtx) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ loadId, label, expiresAt }: CreateTrackingLinkVars) => {
      const body: Record<string, unknown> = {};
      if (label) body['label'] = label;
      if (expiresAt) body['expiresAt'] = expiresAt;
      return api<{ link: LoadTrackingLinkWithToken }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/tracking-link`,
        { method: 'POST', body },
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['loads', fleetId, 'tracking-links', vars.loadId],
      });
    },
  });
}

interface RevokeTrackingLinkVars {
  loadId: string;
  linkId: string;
}

export function useRevokeTrackingLink({ fleetId }: MutationCtx) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ loadId, linkId }: RevokeTrackingLinkVars) =>
      api(
        `/v1/fleets/${fleetId}/loads/${loadId}/tracking-link/${linkId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['loads', fleetId, 'tracking-links', vars.loadId],
      });
    },
  });
}

// ============================================================
// S50 — stop management + load patch
// ============================================================

interface AddStopVars {
  loadId: string;
  body: CreateStopBody;
}
export function useAddStop({ fleetId }: MutationCtx) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, body }: AddStopVars) =>
      api<{ stop: LoadStop }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/stops`,
        { method: 'POST', body },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      qc.invalidateQueries({ queryKey: ['loads', fleetId] });
    },
  });
}

interface UpdateStopVars {
  loadId: string;
  stopId: string;
  body: UpdateStopBody;
}
export function useUpdateStop({ fleetId }: MutationCtx) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, stopId, body }: UpdateStopVars) =>
      api<{ stop: LoadStop }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/stops/${stopId}`,
        { method: 'PATCH', body },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      qc.invalidateQueries({ queryKey: ['loads', fleetId] });
    },
  });
}

interface AdvanceStopVars {
  loadId: string;
  stopId: string;
  reason: string;
  /** Unit D: same key across taps/retries of one advance; API dedupes on it. */
  idempotencyKey?: string;
  /** Unit D: the rung the dispatcher was looking at; API 409s if it moved. */
  expectedStatus?: string;
}
/**
 * Dispatcher escape hatch (walk-test P1-6): advance a stop exactly ONE rung
 * (pending -> en_route -> arrived -> completed) with a required reason,
 * audited as an override. 409 if the stop is already terminal.
 */
export function useAdvanceStop({ fleetId }: MutationCtx) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, stopId, reason, idempotencyKey, expectedStatus }: AdvanceStopVars) =>
      api<{ stop: LoadStop; load: Load; transitions: LoadStatus[]; motion: LoadMotion }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/stops/${stopId}/advance`,
        {
          method: 'POST',
          body: {
            reason,
            ...(idempotencyKey ? { idempotencyKey } : {}),
            ...(expectedStatus ? { expectedStatus } : {}),
          },
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      qc.invalidateQueries({
        queryKey: ['loads', fleetId, 'history', vars.loadId],
      });
      invalidateFleetLoads(qc, fleetId);
    },
  });
}

interface UpdateLoadVars {
  loadId: string;
  body: UpdateLoadBody;
}
export function useUpdateLoad({ fleetId }: MutationCtx) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, body }: UpdateLoadVars) =>
      api<{ load: Load }>(
        `/v1/fleets/${fleetId}/loads/${loadId}`,
        { method: 'PATCH', body },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
      invalidateFleetLoads(qc, fleetId);
    },
  });
}

// -------- Items (create-load flow) --------

interface AddItemVars {
  loadId: string;
  body: { loadStopId: string; description: string; quantity: number; weightKg?: number };
}
export function useAddItem({ fleetId }: MutationCtx) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, body }: AddItemVars) =>
      api<{ item: unknown }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/items`,
        { method: 'POST', body },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: qk.loads.detail(fleetId, vars.loadId) });
    },
  });
}

/**
 * POST .../loads/:loadId/duplicate — copies the commercial shape into a
 * fresh draft (stops + items; schedule/assignment/execution state cleared).
 * Invalidates the loads list; the caller navigates to the returned draft.
 */
export function useDuplicateLoad(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (loadId: string) =>
      api<{ load: Load }>(`/v1/fleets/${fleetId}/loads/${loadId}/duplicate`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.loads.all(fleetId ?? '') });
    },
  });
}

